import { HubConnection, HubConnectionBuilder, HubConnectionState, type IHttpConnectionOptions, LogLevel } from "@microsoft/signalr";



import type { ICustomerSelectItemToPayCmd as ICustomerSelectItemsToPayCmd } from "@/data/commands/customer/CustomerSelectItemToPayCmd";
import { Factory } from "@/data/dao/Factory";
import { parseCollection } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { OperatorSocket } from "@/data/dao/socket/OperatorSocket";
import type { ISelectItemsToPayResult } from "@/data/dao/socket/model/SelectItemsToPayResult";
import { dataEvents } from "@/data/events/dataEvents";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
import type { DeallocationReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent, IAddedOrChangedLinkedFulfilment, IAddedOrChangedLinkedFulfilments, IAllocationuseCaseChangedEvent, IBillChangedEvent, IBillChangedSharedEvent, IBillClosedEvent, IBillDiscountChangedEvent, IBillDiscountChangedSharedEvent, IBillEvent, IBillManualEndUsersCountChangedEvent, IBillNameChangedEvent, IBillOpenedEvent, ICartChangedEvent, ICartDiscountChangedEvent, ICertificateEncodingProgressEvent, ICustomerFulfillmentChangedEvent, ICustomerLinkedFulfillmentsChangedEvent, ICustomerOfferNeedsRefreshEvent, IEmailConfirmedEvent, IFulfillmentPositionsChangedEvent, IGuestNumberChangedEvent, IIdentityDiscoveredEvent, IIdentityEmailChangedEvent, ILinkedFulfillmentPositionsChangedEvent, IProfileChangedEvent, IRoomOfferActivationChangedEvent, UserAllocatedEvent, UserDeallocatedEvent } from "@/data/model/Events";
import { AuthTokenService } from "@/data/services/AuthTokenService";
import { config } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Logging } from "@/data/util/Logging";
import { Signal, TypedSignal } from "@/data/util/Signal";



import { logError } from "@/shared/logging/errors";



import { ConnectionState } from "@/components/websocket/ConnectionState";
import { SocketEventParser } from "./SocketEventParser";


export class AppSocket {
    private hubConnection: HubConnection;
    private authTokenService: AuthTokenService;

    public connectionId: string | null | undefined;

    public onConnectionChanged: TypedSignal<HubConnectionState>;
    public onReconnected: Signal;

    public onItemSelectionChanged?: { (event: BillItemSelectionChangedEvent) };

    public onAllocationChange?: { (reason?: DeallocationReason) };
    public onAllocationUseCaseChange?: { (event: IAllocationuseCaseChangedEvent) };
    public onGuestNumberChanged?: { (event: IGuestNumberChangedEvent) };
    public onEmailConfirmed?: { (event: IEmailConfirmedEvent) };
    public onIdentityEmailChanged?: { (event: IIdentityEmailChangedEvent) };
    public onProfileChanged?: { (event: IProfileChangedEvent) };
    public onIdentityDiscovered?: { (cart?: IIdentityDiscoveredEvent) };
    public onCartChanged?: { (event?: ICartChangedEvent) };
    public onCartDiscountChanged?: { (event?: ICartDiscountChangedEvent) };
    public onFulfillmentChanged?: { (event: ICustomerFulfillmentChangedEvent) };
    public onLinkedFulfillmentsChanged?: { (event: ICustomerLinkedFulfillmentsChangedEvent) };
    public onFulfillmentPositionsChanged?: { (event: IFulfillmentPositionsChangedEvent) };
    public onLinkedFulfillmentPositionsChanged?: { (event: ILinkedFulfillmentPositionsChangedEvent) };
    public onPaymentConfirmed?: { (confirmation: IOrderPaymentConfirmation) };
    public onCertificateEncodingProgress?: { (confirmation: ICertificateEncodingProgressEvent) };
    public onBillOpened?: { (eventData: IBillOpenedEvent) };
    public onBillChanged?: { (eventData: IBillChangedEvent) };
    public onBillChangedShared?: { (eventData: IBillChangedSharedEvent) };
    public onBillDiscountChanged?: { (eventData: IBillDiscountChangedEvent) };
    public onBillDiscountChangedShared?: { (eventData: IBillDiscountChangedSharedEvent) };
    public onBillClosed?: { (eventData: IBillClosedEvent) };
    public onTransferBillItemsDisabled?: { (eventData: IBillClosedEvent) };

    public onCreditCardAttached?: { (card: ICreditCard) };
    public onCreditCardDetached?: { (cardId: string) };

    constructor() {
        this.authTokenService = Factory.getInstance<AuthTokenService>(AuthTokenService);

        this.onConnectionChanged = new TypedSignal<HubConnectionState>();
        this.onReconnected = new Signal();
        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => this.authTokenService.getBearer() ?? "",
        };

        this.hubConnection = new HubConnectionBuilder()
            .withUrl(!config.isMockClient ? "/socket/app" : config.mockClientAppSocketAddress, options)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: () => {
                    return 2000;
                },
            })
            .configureLogging(LogLevel.Critical)
            .build();

        this.hubConnection.onreconnecting(() => {
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.onclose(() => {
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.onreconnected(() => {
            this.onReconnected.dispatch();
            this.connectionId = this.hubConnection.connectionId;
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        });

        this.hubConnection.on("ItemSelectionChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: ItemSelectionChanged");
            const eventData = ev as BillItemSelectionChangedEvent;
            dataEvents.appSocket.ItemSelectionChanged.dispatch(eventData);

            if (this.onItemSelectionChanged !== undefined) {
                this.onItemSelectionChanged(eventData);
            }
        });

        this.hubConnection.on("AllocationChange", (reason?: any) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: AllocationChange");
            const eventData = reason != null ? parseInt(reason) : undefined;
            dataEvents.appSocket.AllocationChange.dispatch(eventData);

            if (this.onAllocationChange !== undefined) {
                this.onAllocationChange(eventData);
            }
        });

        this.hubConnection.on("UserAllocated", (ev: UserAllocatedEvent) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: UserAllocated");
            dataEvents.appSocket.UserAllocated.dispatch(ev);
        });

        this.hubConnection.on("UserDeallocated", (ev: UserDeallocatedEvent) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: UserDeallocated");
            dataEvents.appSocket.UserDeallocated.dispatch(ev);
        });

        this.hubConnection.on("AllocationUseCaseChange", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: AllocationUseCaseChange");
            const eventData = AppSocket.parseAllocationUseCaseChangedEvent(ev);
            dataEvents.appSocket.AllocationUseCaseChange.dispatch(eventData);

            if (this.onAllocationUseCaseChange !== undefined) {
                this.onAllocationUseCaseChange(eventData);
            }
        });

        this.hubConnection.on("GuestNumberChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: GuestNumberChanged");
            const eventData = AppSocket.parseGuestNumberChangedEvent(ev);

            if (this.onGuestNumberChanged !== undefined) {
                this.onGuestNumberChanged(eventData);
            }
        });

        this.hubConnection.on("EmailConfirmed", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: EmailConfirmed");
            const eventData = AppSocket.parseEmailConfirmedEvent(ev);
            dataEvents.appSocket.EmailConfirmed.dispatch(eventData);

            if (this.onEmailConfirmed !== undefined) {
                this.onEmailConfirmed(eventData);
            }
        });

        this.hubConnection.on("CartChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: CartChanged");
            const eventData = AppSocket.parseCartChangedEvent(ev);
            dataEvents.appSocket.CartChanged.dispatch(eventData);

            if (this.onCartChanged !== undefined) {
                this.onCartChanged(eventData);
            }
        });

        this.hubConnection.on("CartDiscountChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: CartDiscountChanged");
            const eventData = AppSocket.parseCartDiscountChangedEvent(ev);
            dataEvents.appSocket.CartDiscountChanged.dispatch(eventData);

            if (this.onCartDiscountChanged !== undefined) {
                this.onCartDiscountChanged(eventData);
            }
        });

        this.hubConnection.on("OfferChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: OfferChanged");
            const eventData = AppSocket.parseCustomerOfferNeedsRefreshEvent(ev);
            dataEvents.appSocket.OfferChanged.dispatch(eventData);
        });

        this.hubConnection.on("PaymentConfirmed", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: PaymentConfirmed");
            const eventData = CustomerDao.parseOrderPaymentConfirmation(ev);
            dataEvents.appSocket.PaymentConfirmed.dispatch(eventData);

            if (this.onPaymentConfirmed !== undefined) {
                this.onPaymentConfirmed(eventData);
            }
        });

        this.hubConnection.on("FulfillmentChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: FulfillmentChanged");
            const eventData = SocketEventParser.parseCustomerFulfillmentChangedEvent(ev);
            dataEvents.appSocket.FulfillmentChanged.dispatch(eventData);

            if (this.onFulfillmentChanged !== undefined) {
                this.onFulfillmentChanged(eventData);
            }
        });

        this.hubConnection.on("LinkedFulfillmentsChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: LinkedFulfillmentsChanged");
            const eventData = AppSocket.parseCustomerLinkedFulfillmentsChangedEvent(ev);
            dataEvents.appSocket.LinkedFulfillmentsChanged.dispatch(eventData);

            if (this.onLinkedFulfillmentsChanged !== undefined) {
                this.onLinkedFulfillmentsChanged(eventData);
            }
        });

        this.hubConnection.on("FulfillmentPositionsChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: FulfillmentPositionsChanged");
            const eventData = AppSocket.parseFulfillmentPositionsChangedEvent(ev);
            dataEvents.appSocket.FulfillmentPositionsChanged.dispatch(eventData);

            if (this.onFulfillmentPositionsChanged !== undefined) {
                this.onFulfillmentPositionsChanged(eventData);
            }
        });

        this.hubConnection.on("LinkedFulfillmentsPositionsChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: LinkedFulfillmentsPositionsChanged");
            const eventData = AppSocket.parseLinkedFulfillmentsPositionsChangedEvent(ev);
            dataEvents.appSocket.LinkedFulfillmentsPositionsChanged.dispatch(eventData);

            if (this.onLinkedFulfillmentPositionsChanged !== undefined) {
                this.onLinkedFulfillmentPositionsChanged(eventData);
            }
        });

        this.hubConnection.on("IdentityDiscovered", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: IdentityDiscovered");
            const eventData = AppSocket.parseIdentityDiscoveredEvent(ev);
            dataEvents.appSocket.IdentityDiscovered.dispatch(eventData);

            if (this.onIdentityDiscovered !== undefined) {
                this.onIdentityDiscovered(eventData);
            }
        });

        this.hubConnection.on("CreditCardAttached", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: CreditCardAttached");
            const eventData = ProfileDao.parseCreditCard(ev);
            dataEvents.appSocket.CreditCardAttached.dispatch(eventData);

            if (this.onCreditCardAttached !== undefined) {
                this.onCreditCardAttached(eventData);
            }
        });

        this.hubConnection.on("CreditCardDetached", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: CreditCardDetached");
            const eventData = ev;
            dataEvents.appSocket.CreditCardDetached.dispatch(eventData);

            if (this.onCreditCardDetached !== undefined) {
                this.onCreditCardDetached(eventData);
            }
        });

        this.hubConnection.on("CertificateEncodingProgress", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: CertificateEncodingProgress");
            const eventData = AppSocket.parseCertificateEncodingProgressEvent(ev);
            dataEvents.appSocket.CertificateEncodingProgress.dispatch(eventData);

            if (this.onCertificateEncodingProgress !== undefined) {
                this.onCertificateEncodingProgress(eventData);
            }
        });

        this.hubConnection.on("RoomOfferActivationChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: RoomOfferActivationChanged");
            const eventData = AppSocket.parseRoomOfferActivationChangedEvent(ev);
            dataEvents.appSocket.RoomOfferActivationChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillOpened", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillOpened");
            const eventData = AppSocket.parseBillOpenedEvent(ev);
            dataEvents.appSocket.BillOpened.dispatch(eventData);

            if (this.onBillOpened !== undefined) {
                this.onBillOpened(eventData);
            }
        });

        this.hubConnection.on("BillChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillChanged");
            const eventData = AppSocket.parseBillChangedEvent(ev);
            dataEvents.appSocket.BillChanged.dispatch(eventData);

            if (this.onBillChanged !== undefined) {
                this.onBillChanged(eventData);
            }
        });

        this.hubConnection.on("BillChangedShared", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillChangedShared");
            const eventData = AppSocket.parseBillChangedSharedEvent(ev);
            dataEvents.appSocket.BillChangedShared.dispatch(eventData);

            if (this.onBillChangedShared !== undefined) {
                this.onBillChangedShared(eventData);
            }
        });

        this.hubConnection.on("BillDiscountChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillDiscountChanged");
            const eventData = AppSocket.parseBillDiscountChangedEvent(ev);
            dataEvents.appSocket.BillDiscountChanged.dispatch(eventData);

            if (this.onBillDiscountChanged !== undefined) {
                this.onBillDiscountChanged(eventData);
            }
        });

        this.hubConnection.on("BillDiscountChangedShared", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillDiscountChangedShared");
            const eventData = AppSocket.parseBillDiscountChangedSharedEvent(ev);
            dataEvents.appSocket.BillDiscountChangedShared.dispatch(eventData);

            if (this.onBillDiscountChangedShared !== undefined) {
                this.onBillDiscountChangedShared(eventData);
            }
        });

        this.hubConnection.on("BillNameChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillNameChanged");
            const eventData = AppSocket.parseBillNameChangedEvent(ev);
            dataEvents.appSocket.BillNameChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillManualEndUsersCountChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillManualEndUsersCountChanged");
            const eventData = AppSocket.parseBillManualEndUsersCountChangedEvent(ev);
            dataEvents.appSocket.BillManualEndUsersCountChanged.dispatch(eventData);
        });

        this.hubConnection.on("BillClosed", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: BillClosed");
            const eventData = AppSocket.parseBillClosedEvent(ev);
            dataEvents.appSocket.BillClosed.dispatch(eventData);

            if (this.onBillClosed !== undefined) {
                this.onBillClosed(eventData);
            }
        });

        this.hubConnection.on("TransferBillItemsDisabled", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: TransferBillItemsDisabled");
            const eventData = AppSocket.parseBillEvent(ev);
            dataEvents.appSocket.TransferBillItemsDisabled.dispatch(eventData);
        });

        this.hubConnection.on("UserSignedOut", () => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: UserSignedOut");
            dataEvents.appSocket.UserSignedOut.dispatch();
        });

        this.hubConnection.on("IdentityEmailChanged", (ev?: any) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: IdentityEmailChanged");
            const eventData = AppSocket.parseIdentityEmailChangedEvent(ev);
            dataEvents.appSocket.IdentityEmailChanged.dispatch(eventData);

            if (this.onIdentityEmailChanged !== undefined) {
                this.onIdentityEmailChanged(eventData);
            }
        });

        this.hubConnection.on("ProfileChanged", (ev?: any) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: ProfileChanged");
            const eventData = AppSocket.parseProfileChangedEvent(ev);
            dataEvents.appSocket.ProfileChanged.dispatch(eventData);

            if (this.onProfileChanged !== undefined) {
                this.onProfileChanged(eventData);
            }
        });

        this.hubConnection.on("TableCovidUserCountChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: TableCovidUserCountChanged");
            const eventData = OperatorSocket.parseTableCovidUserCountChangedEvent(ev);
            dataEvents.appSocket.TableCovidUserCountChanged.dispatch(eventData);
        });

        this.hubConnection.on("UserSessionExpired", () => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: UserSessionExpired");
            dataEvents.appSocket.UserSessionExpired.dispatch();
        });

        this.hubConnection.on("RoomPlanChanged", (ev) => {
            if (!this.isEnabled) return;

            Logging.LogSocketNotification("AppSocket: RoomPlanChanged");
            const eventData = OperatorSocket.parseRoomPlanChangedEvent(ev);
            dataEvents.appSocket.RoomPlanChanged.dispatch(eventData);
        });
    }

    public async connect(): Promise<void> {
        await this.disconnect();

        if (config.isMockClient) {
            return;
        }

        try {
            await this.hubConnection.start();
            this.connectionId = this.hubConnection.connectionId;
            this.onConnectionChanged.dispatch(this.hubConnection.state);
        } catch (ex) {
            logError(ex);
            this.connectionId = undefined;
            this.onConnectionChanged.dispatch(this.hubConnection.state);
            throw new Error("Could not connect socket");
        }
    }

    public async disconnect(): Promise<void> {
        if (config.isMockClient) return;

        await this.hubConnection.stop();
        this.connectionId = undefined;
    }

    public async stopStart(): Promise<void> {
        ConnectionState.disabled = true;
        try {
            await this.hubConnection.stop();
            await this.connect();
            this.connectionId = this.hubConnection.connectionId;
        } catch (ex) {
            logError(ex);
            this.connectionId = undefined;
            throw ex;
        } finally {
            ConnectionState.disabled = false;
        }
    }

    public async checkConnectivity(): Promise<void> {
        this.onConnectionChanged.dispatch(this.hubConnection.state);

        const statesToSkip = [
            HubConnectionState.Connected,
            HubConnectionState.Connecting,
            HubConnectionState.Reconnecting,
        ];

        if (statesToSkip.includes(this.hubConnection.state)) {
            return;
        }

        try {
            await this.hubConnection.stop();
            await this.connect();
        } catch (ex) {
            logError(ex);
            await this.connect();
        }
    }

    private isEnabled = true;
    public disable() {
        this.isEnabled = false;
    }

    public enable() {
        this.isEnabled = true;
    }

    public async selectItemsToPay(cmd: ICustomerSelectItemsToPayCmd): Promise<ISelectItemsToPayResult> {
        if (config.isMockClient) {
            return {
                isSuccess: true,
                exceptionCode: undefined,
            };
        }

        return await this.hubConnection.invoke("SelectItemsToPay", cmd);
    }

    public dispose() {
        this.onConnectionChanged.removeAll();
        this.onReconnected.removeAll();

        this.onItemSelectionChanged = undefined;

        this.onAllocationChange = undefined;
        this.onAllocationUseCaseChange = undefined;
        this.onGuestNumberChanged = undefined;
        this.onEmailConfirmed = undefined;
        this.onIdentityDiscovered = undefined;
        this.onCartChanged = undefined;

        this.onFulfillmentChanged = undefined;
        this.onFulfillmentPositionsChanged = undefined;
        this.onPaymentConfirmed = undefined;
        this.onCertificateEncodingProgress = undefined;

        this.onCreditCardAttached = undefined;
        this.onCreditCardDetached = undefined;
    }

    private static parseGuestNumberChangedEvent(literal: any): IGuestNumberChangedEvent {
        return {
            guestNumber: parseInt(literal.guestNumber),
            identityId: literal.identityId,
        };
    }

    private static parseEmailConfirmedEvent(literal: any): IEmailConfirmedEvent {
        return {
            newEmail: literal.newEmail,
        };
    }

    private static parseIdentityDiscoveredEvent(literal: any): IIdentityDiscoveredEvent {
        return {
            discoveredFirstName: literal.discoveredFirstName == null ? undefined : literal.discoveredFirstName,
            discoveredLastName: literal.discoveredLastName == null ? undefined : literal.discoveredLastName,
        };
    }

    private static parseCustomerLinkedFulfillmentsChangedEvent(literal: any): ICustomerLinkedFulfillmentsChangedEvent {
        return {
            linkedFulfillment: AppSocket.parseAddedOrChangedLinkedFulfilments(literal.linkedFulfillment),
            identityId: literal.identityId,
            orderId: literal.orderId,
            locationId: literal.locationId,
        };
    }

    private static parseAddedOrChangedLinkedFulfilments(literal: any): IAddedOrChangedLinkedFulfilments {
        return {
            fulfillments: parseCollection(literal.fulfillments, AppSocket.parseAddedOrChangedLinkedFulfilment),
            fulfillmentState: parseInt(literal.fulfillmentState),
            fulfillmentPreparationStartDateTime: DateTime.parseIsoDate(literal.fulfillmentPreparationStartDateTime),
        };
    }

    private static parseAddedOrChangedLinkedFulfilment(literal: any): IAddedOrChangedLinkedFulfilment {
        return {
            id: literal.id,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            locationId: literal.locationId,
        };
    }

    private static parseCartChangedEvent(literal: any): ICartChangedEvent {
        return {
            cart: literal.cart == null ? undefined : CustomerDao.parseCustomerCart(literal.cart),
            isUserAction: literal.isUserAction,
        };
    }

    private static parseCartDiscountChangedEvent(literal: any): ICartDiscountChangedEvent {
        return {
            cart: literal.cart == null ? undefined : CustomerDao.parseCustomerCart(literal.cart),
        };
    }

    private static parseCustomerOfferNeedsRefreshEvent(literal: any): ICustomerOfferNeedsRefreshEvent {
        return {
            locationId: literal.locationId,
            roomId: literal.roomId,
        };
    }

    private static parseCertificateEncodingProgressEvent(literal: any): ICertificateEncodingProgressEvent {
        return {
            progress: parseFloat(literal.progress),
            state: literal.state,
        };
    }

    private static parseAllocationUseCaseChangedEvent(literal: any): IAllocationuseCaseChangedEvent {
        return {
            locationId: literal.locationId,
            serviceId: literal.serviceId,
            tableId: literal.tableId,
            newUseCase: literal.newUseCase == null ? undefined : PlanningDao.parseOrderApplication(literal.newUseCase),
        };
    }

    private static parseFulfillmentPositionsChangedEvent(literal: any): IFulfillmentPositionsChangedEvent {
        return {
            locationId: literal.locationId,
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
        };
    }

    private static parseLinkedFulfillmentsPositionsChangedEvent(literal: any): ILinkedFulfillmentPositionsChangedEvent {
        return {
            locationId: literal.locationId,
            fulfillmentGroupIds: literal.fulfillmentGroupIds,
        };
    }

    private static parseRoomOfferActivationChangedEvent(literal: any): IRoomOfferActivationChangedEvent {
        return {
            locationId: literal.locationId,
            roomId: literal.roomId,
            offerId: literal.offerId,
        };
    }

    public static parseBillOpenedEvent(literal: any): IBillOpenedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            bill: CustomerDao.parseBill(literal.bill)!,
        };
    }

    public static parseBillDiscountChangedEvent(literal: any): IBillDiscountChangedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            bill: CustomerDao.parseBill(literal.bill)!,
        };
    }

    public static parseBillDiscountChangedSharedEvent(literal: any): IBillDiscountChangedSharedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            bill: CustomerDao.parseBill(literal.bill)!,
            identitiesToIgnore: literal.identitiesToIgnore,
        };
    }

    public static parseBillChangedEvent(literal: any): IBillChangedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            bill: CustomerDao.parseBill(literal.bill)!,
        };
    }

    public static parseBillChangedSharedEvent(literal: any): IBillChangedSharedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            bill: CustomerDao.parseBill(literal.bill)!,
            identitiesToIgnore: literal.identitiesToIgnore,
        };
    }

    public static parseBillNameChangedEvent(literal: any): IBillNameChangedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            name: literal.name,
        };
    }

    public static parseBillManualEndUsersCountChangedEvent(literal: any): IBillManualEndUsersCountChangedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            manualEndUsersCount: parseInt(literal.manualEndUsersCount),
        };
    }

    public static parseBillClosedEvent(literal: any): IBillClosedEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
            billType: parseInt(literal.type),
        };
    }

    public static parseBillEvent(literal: any): IBillEvent {
        return {
            locationId: literal.locationId,
            tableId: literal.tableId,
            billId: literal.billId,
        }
    }

    private static parseIdentityEmailChangedEvent(literal: any): IIdentityEmailChangedEvent {
        return {
            identityId: literal.identityId,
            email: literal.email,
        };
    }

    private static parseProfileChangedEvent(literal: any): IProfileChangedEvent {
        return {
            identityId: literal.identityId,
            firstName: literal.firstName == null ? undefined : literal.firstName,
            lastName: literal.lastName == null ? undefined : literal.lastName,
            phoneNumber: literal.phoneNumber == null ? undefined : literal.phoneNumber,
            email: literal.email == null ? undefined : literal.email,
        };
    }
}
